'use client';

import type { SxProps } from '@mui/material';
import { Box, TableContainer, Typography } from '@mui/material';
import type {
  Gallery,
  GalleryKeywordFieldKey,
  GallerySimple,
  GalleryStampKindKey,
  PagerData,
} from '@ocode/domain';
import { ApiRequestContext } from '@ocodelib/api-common';
import { flatSx, FlexColumn } from '@ocodelib/mui';
import { useDebounceEffect } from '@ocodelib/mui/hooks';
import { BbsPagination } from '@repo-foundation/components/BbsPagination';
import { sxChildSpaceY } from '@repo-foundation/util/sx-util';
import { NoDataView } from '@repo-ui/components/NoDataView';
import { OpenLectureListRow } from '@repo-ui/components/OpenLectureListRow';
import {
  type GalleryPreviewDialogProps,
  GalleryPreviewDialog,
} from '@repo-ui/dialogs/GalleryPreviewDialog';
import { useApi } from '@repo-ui/provider/useApi';
import { handleError } from '@repo-ui/util/handle-error';
import clsx from 'clsx';
import { useCallback, useState } from 'react';

type DialogParams = {
  id: 'GalleryPreviewDialog';
  props: GalleryPreviewDialogProps;
};

const DEFAULT_SEARCH_OPTS = {
  rowsPerPage: 20,
  pageNumber: 0,
};

type SearchParams = {
  rowsPerPage: number;
  pageNumber: number;

  cate?: string;

  /**
   * 검색 키워드의 필드
   * 전체, 제목, 작성자이름
   */
  keywordField?: GalleryKeywordFieldKey;

  /**
   * 최대 30글자
   */
  keyword?: string;

  /**
   * 일반,우수,인기 구분
   * 값을 전달하지 않으면 전체 조회
   */
  stampKind?: GalleryStampKindKey;
};

interface Props {
  cate?: string;

  /**
   * 일반,우수,인기 구분
   * 값을 전달하지 않으면 전체 조회
   */
  stampKind?: GalleryStampKindKey;
  sx?: SxProps;
  className?: string;
}

/**
 * 공개 갤러리 조회
 * 전체, 우수, 인기 작품 조회
 */
export function PublicLectureList(props: Props) {
  const { stampKind, cate, sx, className } = props;
  const api = useApi();
  const [searchParams, setSearchParams] = useState(() => ({
    ...DEFAULT_SEARCH_OPTS,
    stampKind,
    breed: props.cate,
  }));
  const [pagerData, setPagerData] = useState<PagerData<GallerySimple>>();
  const [loading, setLoading] = useState(false);
  const [dialogParams, setDialogParams] = useState<DialogParams>();
  const [refreshToken, setRefreshToken] = useState(0);

  const doLoadGalleryList = useCallback(
    async (ctx: ApiRequestContext, params: SearchParams, cate?: string) => {
      try {
        setLoading(true);
        const { pagerData } = await api.gallery.search({ ctx, ...params, breed: [] });
        if (ctx.canceled) return;
        setPagerData(pagerData);
      } catch (err) {
        handleError(err);
      } finally {
        setLoading(false);
      }
    },
    [api],
  );

  useDebounceEffect(
    () => {
      const ctx = ApiRequestContext.of();
      doLoadGalleryList(ctx, searchParams, cate);
      return () => {
        ctx.cancel();
      };
    },
    [doLoadGalleryList, searchParams, refreshToken, cate],
    { wait: 200 },
  );

  // 갤러리 업데이트
  const updateGallery = (newGallery: GallerySimple) => {
    const elements = pagerData?.elements;
    if (!elements) return;
    const idx = elements.findIndex((it) => it.galleryId === newGallery.galleryId);
    if (idx < 0) return;
    const cloned: GallerySimple = {
      ...elements[idx]!,
      seenCount: newGallery.seenCount,
      favorCount: newGallery.favorCount,
      chimCount: newGallery.chimCount,
      isFavor: newGallery.isFavor,
      isChim: newGallery.isChim,
    };

    const newPagerData = {
      ...pagerData,
      elements: elements.map((it) => (it.galleryId === cloned.galleryId ? cloned : it)),
    };
    setPagerData(newPagerData);
  };

  const handleClickRow = (gallery: GallerySimple) => () => {
    const galleryId = gallery.galleryId;
    setDialogParams({
      id: 'GalleryPreviewDialog',
      props: {
        galleryId,
        open: true,
        onClose: (changedGallery?: Gallery) => {
          setDialogParams(undefined);
          if (changedGallery) {
            updateGallery(changedGallery);
          }
        },
        onDeleted: () => {
          setRefreshToken(Date.now());
          setDialogParams(undefined);
        },
      },
    });
  };

  // 페이지 번호 클릭
  const handleClickPageNumber = (pageNumber: number) => {
    setSearchParams((p) => ({ ...p, pageNumber }));
  };

  const {
    totalPages = 0,
    pageNumber = 0,
    elements = [],
    offset = 0,
    totalElements = 0,
  } = pagerData ?? {};

  const itemNumMax = totalElements - offset;

  return (
    <Box className={clsx('PublicLectureList-root', className)} sx={flatSx({ pb: 5 }, sx)}>
      <FlexColumn
        sx={{
          mt: 3,
        }}
      >
        <NoDataView visible={!loading && totalElements === 0} title="작품이 없습니다." />

        {totalElements > 0 && (
          <Typography variant="body1" sx={{ mb: 1, color: '#888', '& b': { color: '#444' } }}>
            전체 <b>{totalElements}</b>개
          </Typography>
        )}
        <TableContainer
          sx={{
            p: 0.2,
            '& .OpenLectureListRow-root': {
              minWidth: 650,
            },
            ...sxChildSpaceY(3),
          }}
        >
          {elements.map((gal, i) => (
            <OpenLectureListRow key={gal.galleryId} onClick={handleClickRow(gal)} gallery={gal} />
          ))}
        </TableContainer>
      </FlexColumn>

      <BbsPagination
        page={pageNumber}
        count={totalPages}
        disabled={loading}
        onChange={handleClickPageNumber}
      />

      {dialogParams?.id === 'GalleryPreviewDialog' && (
        <GalleryPreviewDialog {...dialogParams.props} />
      )}
    </Box>
  );
}
